import { useEffect, useState, useCallback, useRef } from "react";
import { useLang } from "@/contexts/LanguageContext";
import { useSiteContent } from "@/contexts/SiteContentContext";
import { api } from "@/lib/api";
import { Camera, ChevronLeft, ChevronRight, X } from "lucide-react";

const Gallery = () => {
  const { lang } = useLang();
  const { content } = useSiteContent();
  const [galleries, setGalleries] = useState([]);

  // Carousel state
  const data = content?.showcase_gallery;
  const images = data?.images || [];
  const total = images.length;
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    api
      .get("/galleries")
      .then((r) => setGalleries((r.data || []).filter((g) => (g.images || []).length > 0)))
      .catch(() => {});
  }, []);

  const go = useCallback((delta) => {
    if (total === 0) return;
    setIdx((i) => (i + delta + total) % total);
  }, [total]);

  useEffect(() => {
    if (paused || total < 2) return undefined;
    const id = setInterval(() => setIdx((i) => (i + 1) % total), 4500);
    return () => clearInterval(id);
  }, [paused, total]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "ArrowRight") go(1);
      if (e.key === "Escape") setLightbox(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go]);

  const prev = total > 0 ? (idx - 1 + total) % total : 0;
  const next = total > 0 ? (idx + 1) % total : 0;

  return (
    <section id="gallery" className="relative py-20 md:py-28 overflow-hidden" style={{ background: "linear-gradient(180deg, #FDFBF7 0%, #F5EFE6 100%)" }}>
      {/* Decorative blobs */}
      <div aria-hidden className="absolute -top-40 -right-40 w-[480px] h-[480px] rounded-full bg-[#AC5D45]/10 blur-3xl" />
      <div aria-hidden className="absolute -bottom-40 -left-40 w-[420px] h-[420px] rounded-full bg-[#1E3A5F]/8 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-5 md:px-10 lg:px-12">
        {/* === BANNER: Title + 3D Casa lontano da casa === */}
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#AC5D45] font-semibold mb-3">
            <Camera className="w-3.5 h-3.5" />
            La Struttura
          </span>
          <h2 className="font-display text-4xl md:text-6xl text-[#1E3A5F] leading-[1.05] mb-6">
            Vivi gli ambienti
          </h2>
        </div>

        <div className="flex justify-center mb-14" data-testid="struttura-quote-3d">
          <div
            className="relative w-full max-w-3xl rounded-2xl px-8 py-7 md:px-10 md:py-9 text-center"
            style={{
              background: "linear-gradient(135deg, #1E3A5F 0%, #2A4F7C 45%, #AC5D45 100%)",
              boxShadow: "0 30px 80px -25px rgba(30,58,95,0.6), inset 0 1px 0 rgba(255,255,255,0.22), inset 0 -2px 0 rgba(0,0,0,0.22)",
              transform: "perspective(1100px) rotateX(2.5deg)",
            }}
          >
            <span
              aria-hidden
              className="pointer-events-none absolute inset-0 rounded-2xl"
              style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.20) 0%, rgba(255,255,255,0) 40%, rgba(0,0,0,0.15) 100%)" }}
            />
            <p
              className="relative font-display text-3xl md:text-4xl italic text-white leading-snug text-center"
              style={{ textShadow: "0 2px 8px rgba(0,0,0,0.4)" }}
            >
              "Casa... lontano da casa."
            </p>
            <p className="relative mt-2 text-center normal-case tracking-[0.15em] text-[#FFE9C0]/95 italic text-sm">
              «recensioni ospiti»
            </p>
            <p className="relative mt-2 text-[11px] uppercase tracking-[0.3em] text-[#FFE9C0] text-center">
              la sensazione che vogliamo lasciarvi
            </p>
          </div>
        </div>

        {/* === CAROUSEL inline === */}
        {total > 0 && (
          <div
            data-testid="gallery-carousel"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
          >
            <div className="relative h-[340px] sm:h-[440px] md:h-[520px]">
              {images.map((src, i) => {
                let pos = "hidden";
                if (i === idx) pos = "center";
                else if (i === prev) pos = "left";
                else if (i === next) pos = "right";

                const baseCls = "absolute top-1/2 left-1/2 -translate-y-1/2 transition-all duration-700 ease-out rounded-2xl overflow-hidden shadow-2xl bg-[#1E3A5F]";
                const styleByPos = {
                  center: "w-[78%] sm:w-[60%] md:w-[52%] aspect-[3/4] md:aspect-[16/10] -translate-x-1/2 z-30 opacity-100 cursor-zoom-in",
                  left:   "w-[60%] sm:w-[42%] md:w-[34%] aspect-[3/4] md:aspect-[16/10] -translate-x-[120%] z-20 opacity-50 scale-90 hidden sm:block cursor-pointer",
                  right:  "w-[60%] sm:w-[42%] md:w-[34%] aspect-[3/4] md:aspect-[16/10] translate-x-[20%] z-20 opacity-50 scale-90 hidden sm:block cursor-pointer",
                  hidden: "w-0 h-0 opacity-0 pointer-events-none",
                };

                return (
                  <button
                    type="button"
                    key={`${src}-${i}`}
                    data-testid={`gallery-slide-${i}`}
                    aria-hidden={pos === "hidden"}
                    onClick={() => {
                      if (pos === "center") setLightbox(src);
                      else if (pos === "left") go(-1);
                      else if (pos === "right") go(1);
                    }}
                    className={`${baseCls} ${styleByPos[pos]}`}
                  >
                    <img src={src} alt={`Ambiente ${i + 1}`} className="w-full h-full object-cover" loading={i === idx ? "eager" : "lazy"} />
                  </button>
                );
              })}

              {total > 1 && (
                <>
                  <button type="button" onClick={() => go(-1)} aria-label="Precedente" data-testid="gallery-prev"
                    className="absolute top-1/2 left-3 md:left-6 -translate-y-1/2 z-40 w-11 h-11 md:w-12 md:h-12 rounded-full bg-white/95 backdrop-blur shadow-xl border border-[#E8E2D9] hover:bg-white text-[#1E3A5F] hover:scale-110 transition flex items-center justify-center">
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button type="button" onClick={() => go(1)} aria-label="Successiva" data-testid="gallery-next"
                    className="absolute top-1/2 right-3 md:right-6 -translate-y-1/2 z-40 w-11 h-11 md:w-12 md:h-12 rounded-full bg-white/95 backdrop-blur shadow-xl border border-[#E8E2D9] hover:bg-white text-[#1E3A5F] hover:scale-110 transition flex items-center justify-center">
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}
            </div>

            {/* Dots + counter */}
            <div className="flex flex-col items-center gap-3 mt-8">
              <div className="flex justify-center items-center gap-2 flex-wrap">
                {images.map((src, i) => (
                  <button key={`gallery-dot-${src}-${i}`} type="button" onClick={() => setIdx(i)} aria-label={`Foto ${i + 1}`}
                    data-testid={`gallery-dot-${i}`}
                    className={`block h-1.5 rounded-full transition-all ${i === idx ? "bg-[#AC5D45] w-8" : "bg-[#E8E2D9] w-1.5 hover:bg-[#AC5D45]/40"}`}
                  />
                ))}
              </div>
              <div className="text-xs text-[#635F59] font-mono tabular-nums">
                <strong className="text-[#1E3A5F]">{String(idx + 1).padStart(2, "0")}</strong>
                <span className="mx-1.5 text-[#E8E2D9]">/</span>
                <span>{String(total).padStart(2, "0")}</span>
              </div>
            </div>
          </div>
        )}

        {/* === Room-specific galleries (if admin uploads them) === */}
        {galleries.length > 0 && (
          <div className="mt-20 pt-12 border-t border-[#E8E2D9]">
            {galleries.map((g) => (
              <div key={g.id} className="mb-14 last:mb-0" data-testid={`gallery-${g.slug}`}>
                <div className="flex items-end justify-between gap-4 mb-5 flex-wrap">
                  <div>
                    <h3 className="font-display text-2xl md:text-3xl text-[#1E3A5F]">{g.name?.[lang] || g.name?.it}</h3>
                    {g.description?.[lang] && (
                      <p className="text-sm text-[#635F59] mt-1 max-w-2xl">{g.description?.[lang] || g.description?.it}</p>
                    )}
                  </div>
                  <span className="text-xs text-[#635F59]">{g.images.length} foto</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3">
                  {g.images.map((src, i) => (
                    <a key={`${src}-${i}`} href={src} target="_blank" rel="noreferrer"
                      className={`block overflow-hidden rounded-xl group ${i === 0 ? "col-span-2 row-span-2 md:col-span-2 md:row-span-2 aspect-[4/3]" : "aspect-square"}`}>
                      <img src={src} alt={`${g.name?.it} ${i + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 bg-black/90 z-[200] flex items-center justify-center p-6 animate-fadeIn"
          onClick={() => setLightbox(null)} data-testid="gallery-lightbox">
          <button type="button" onClick={() => setLightbox(null)}
            className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/95 hover:bg-white flex items-center justify-center shadow-xl" aria-label="Chiudi">
            <X className="w-5 h-5" />
          </button>
          <img src={lightbox} alt="" className="max-h-full max-w-full object-contain rounded-lg shadow-2xl" />
        </div>
      )}
    </section>
  );
};

export default Gallery;
